/**
 * P5-04: 前端性能监控
 * Core Web Vitals (CWV) + 3D 场景专属指标采集
 */

interface PerformanceMetrics {
  /** Largest Contentful Paint */
  lcp?: number;
  /** First Contentful Paint */
  fcp?: number;
  /** Cumulative Layout Shift */
  cls?: number;
  /** Total Blocking Time (替代 FID) */
  tbt?: number;
  /** WebGL 初始化耗时 */
  webglInit?: number;
  /** 首帧渲染时间 */
  firstFrame?: number;
  /** 页面加载总耗时 */
  pageLoad?: number;
  /** 3D 场景 FPS 统计 */
  avgFps?: number;
  /** 内存使用估算 (MB) */
  memoryMB?: number;
  /** 页面路径 */
  pagePath: string;
  /** 用户代理 */
  userAgent: string;
  /** 会话 ID */
  sessionId: string;
  timestamp: number;
}

const SESSION_ID = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const REPORT_URL = import.meta.env.VITE_MONITOR_URL || '/api/events';
const SAMPLE_RATE = parseFloat(import.meta.env.VITE_MONITOR_SAMPLE_RATE || '1');

/** 是否采集（采样率控制） */
function shouldCollect(): boolean {
  return Math.random() < SAMPLE_RATE;
}

/** 上报数据 */
function report(metrics: Partial<PerformanceMetrics>) {
  if (!shouldCollect()) return;

  const payload: PerformanceMetrics = {
    pagePath: window.location.pathname,
    userAgent: navigator.userAgent,
    sessionId: SESSION_ID,
    timestamp: Date.now(),
    ...metrics,
  };

  // 使用 navigator.sendBeacon 或 fetch 静默上报
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(REPORT_URL, JSON.stringify(payload));
    } else {
      fetch(REPORT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {}); // 静默失败
    }
  } catch {
    // 监控上报失败不应影响业务
  }
}

/** 采集 Core Web Vitals */
export function initCWVMonitoring() {
  if (typeof window === 'undefined') return;

  // FCP
  const fcpObserver = new PerformanceObserver((list) => {
    const entry = list.getEntries()[0];
    if (entry) {
      report({ fcp: Math.round(entry.startTime) });
    }
  });
  fcpObserver.observe({ type: 'paint', buffered: true });

  // LCP
  const lcpObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    const lastEntry = entries[entries.length - 1];
    if (lastEntry) {
      report({ lcp: Math.round(lastEntry.startTime) });
    }
  });
  lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

  // CLS
  let clsValue = 0;
  const clsObserver = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (!(entry as any).hadRecentInput) {
        clsValue += (entry as any).value;
      }
    }
  });
  clsObserver.observe({ type: 'layout-shift', buffered: true });

  // 页面卸载时上报 CLS
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      report({ cls: Math.round(clsValue * 1000) / 1000 });
    }
  });

  // TBT (Total Blocking Time) - 使用 Long Tasks API
  let tbtValue = 0;
  const tbtObserver = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      const duration = (entry as any).duration;
      if (duration > 50) {
        tbtValue += duration - 50;
      }
    }
  });
  tbtObserver.observe({ type: 'longtask', buffered: true });

  // 页面加载总耗时
  window.addEventListener('load', () => {
    setTimeout(() => {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (nav) {
        report({
          pageLoad: Math.round(nav.loadEventEnd - nav.startTime),
          tbt: Math.round(tbtValue),
        });
      }
    }, 0);
  });
}

/** 3D 场景性能监控 */
export class ScenePerformanceMonitor {
  private frameCount = 0;
  private lastTime = performance.now();
  private fpsSamples: number[] = [];
  private isRunning = false;
  private rafId: number | null = null;

  /** 开始监控 FPS */
  start() {
    if (this.isRunning) return;
    this.isRunning = true;
    this.frameCount = 0;
    this.lastTime = performance.now();
    this.fpsSamples = [];
    this.tick();
  }

  /** 停止监控并上报 */
  stop() {
    this.isRunning = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }

    // 计算平均 FPS
    if (this.fpsSamples.length > 0) {
      const avg = Math.round(
        this.fpsSamples.reduce((a, b) => a + b, 0) / this.fpsSamples.length
      );
      report({ avgFps: avg });
    }
  }

  private tick = () => {
    if (!this.isRunning) return;

    const now = performance.now();
    this.frameCount++;

    if (now - this.lastTime >= 1000) {
      const fps = Math.round((this.frameCount * 1000) / (now - this.lastTime));
      this.fpsSamples.push(fps);
      // 只保留最近 60 个样本
      if (this.fpsSamples.length > 60) {
        this.fpsSamples.shift();
      }
      this.frameCount = 0;
      this.lastTime = now;
    }

    this.rafId = requestAnimationFrame(this.tick);
  };

  /** 记录 WebGL 初始化耗时 */
  static recordWebGLInit(durationMs: number) {
    report({ webglInit: Math.round(durationMs) });
  }

  /** 记录首帧渲染时间 */
  static recordFirstFrame(durationMs: number) {
    report({ firstFrame: Math.round(durationMs) });
  }
}

/** 获取内存使用估算 */
export function getMemoryUsage(): number | undefined {
  const perf = performance as any;
  if (perf.memory) {
    return Math.round(perf.memory.usedJSHeapSize / 1024 / 1024 * 100) / 100;
  }
  return undefined;
}

/** 初始化全部性能监控 */
export function initPerformanceMonitoring() {
  initCWVMonitoring();

  // 定期上报内存使用
  setInterval(() => {
    const mem = getMemoryUsage();
    if (mem && mem > 200) {
      // 仅在高内存时上报
      report({ memoryMB: mem });
    }
  }, 30000);
}
